export type ThemeSetting = "system" | "light" | "dark";

/**
 * How often the preview is redrawn while the playhead is being dragged.
 *
 * The rates are what a person chooses between, so they are what is written down; the
 * stored value is the interval the throttle needs, derived from them. Going the other way
 * loses the rate — 0.5 fps and 1 fps both round back to 1.
 */
export const SCRUB_RATES_FPS: readonly number[] = [30, 15, 10, 5, 1, 0.5];

export const scrubIntervalFor = (fps: number): number => Math.round(1000 / fps);

export const SCRUB_INTERVALS_MS: readonly number[] = SCRUB_RATES_FPS.map(scrubIntervalFor);
export type FileView = "list" | "thumbnail" | "card";

export type Preferences = {
  readonly theme: ThemeSetting;
  readonly lastProjectId: string | null;
  readonly fileView: FileView;
  /** When false, feature packs download without asking first. */
  readonly confirmModuleDownload: boolean;
  readonly scrubIntervalMs: number;
};

export const DEFAULT_PREFERENCES: Preferences = {
  theme: "system",
  lastProjectId: null,
  fileView: "card",
  confirmModuleDownload: true,
  // 5 fps: often enough to find a moment, seldom enough that seeking keeps up.
  scrubIntervalMs: 200,
};

const STORAGE_KEY = "mediaknife.preferences";

const THEMES: readonly ThemeSetting[] = ["system", "light", "dark"];
const FILE_VIEWS: readonly FileView[] = ["list", "thumbnail", "card"];

function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

export function parsePreferences(raw: string | null): Preferences {
  if (!raw) return DEFAULT_PREFERENCES;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return DEFAULT_PREFERENCES;
  }
  if (typeof value !== "object" || value === null) return DEFAULT_PREFERENCES;
  const record = value as Record<string, unknown>;
  return {
    theme: pick(record["theme"], THEMES, DEFAULT_PREFERENCES.theme),
    lastProjectId: typeof record["lastProjectId"] === "string" ? record["lastProjectId"] : null,
    fileView: pick(record["fileView"], FILE_VIEWS, DEFAULT_PREFERENCES.fileView),
    confirmModuleDownload:
      typeof record["confirmModuleDownload"] === "boolean"
        ? record["confirmModuleDownload"]
        : DEFAULT_PREFERENCES.confirmModuleDownload,
    scrubIntervalMs: SCRUB_INTERVALS_MS.includes(record["scrubIntervalMs"] as number)
      ? (record["scrubIntervalMs"] as number)
      : DEFAULT_PREFERENCES.scrubIntervalMs,
  };
}

export function readPreferences(): Preferences {
  try {
    return parsePreferences(localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private-mode browsers can throw on access rather than returning null.
    return DEFAULT_PREFERENCES;
  }
}

export function writePreferences(preferences: Preferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Preferences are a convenience; a full or blocked store must not break editing.
  }
}
